// SWING FEEL: how a swinging band places the second note of a pair, Guitar
// Pro's "triplet feel" choices (user request Oct 2026, with GP's dialog as the
// reference: "the lack of swing is becoming noticeable", and on Libertango
// "some of the notes blur together, it's not straight 16ths").
//
// A feel names a LEVEL and a RATIO. The level is the pair that swings: two
// eighths (a beat) or two sixteenths (half a beat). The ratio is how much of
// the pair the first note takes: the triplet 2/3, dotted 3/4, Scottish 1/4
// (the snap, short-long).
//
// A pair swings only where a part plays it AT that level. Swinging is a way
// of playing two eighths; a beat of four sixteenths under an eighth feel is
// not two eighths, and moving its "and" to the triplet put it a twelfth of a
// beat from the sixteenth after it, where the two blurred into one. So a pair
// in which a part sounds anything finer (a sixteenth, a triplet) stays
// straight for that part, the way Guitar Pro applies its feel to the note
// values it names. Pure: the player asks where a written position sounds.

import type { StyleBand } from './style';

export type SwingFeel =
  | 'triplet-8th'
  | 'triplet-16th'
  | 'dotted-8th'
  | 'dotted-16th'
  | 'scottish-8th'
  | 'scottish-16th';

export const SWING_FEELS: readonly SwingFeel[] = [
  'triplet-8th',
  'triplet-16th',
  'dotted-8th',
  'dotted-16th',
  'scottish-8th',
  'scottish-16th',
];

/** The feel a band swings with when nothing says otherwise: the jazz triplet. */
export const DEFAULT_SWING_FEEL: SwingFeel = 'triplet-8th';

export function isSwingFeel(value: unknown): value is SwingFeel {
  return typeof value === 'string' && (SWING_FEELS as readonly string[]).includes(value);
}

export interface SwingGrid {
  /** Beats one swung pair spans: 1 for eighths, 0.5 for sixteenths. */
  pair: number;
  /** The fraction of the pair the first note takes (0.5 would be straight). */
  ratio: number;
}

export function swingGrid(feel: SwingFeel | undefined): SwingGrid {
  const f = feel ?? DEFAULT_SWING_FEEL;
  const pair = f.endsWith('16th') ? 0.5 : 1;
  const ratio = f.startsWith('dotted') ? 3 / 4 : f.startsWith('scottish') ? 1 / 4 : 2 / 3;
  return { pair, ratio };
}

/** How close a position must sit to a grid point to count as on it, in beats. */
const ON_GRID = 0.02;

function pairIndex(position: number, grid: SwingGrid): number {
  return Math.floor(position / grid.pair + 1e-9);
}

/** The pairs (by index from beat 0) in which a part plays finer than the
 * feel's level. Those pairs stay straight for that part. */
export function straightPairs(onsets: Iterable<number>, grid: SwingGrid): Set<number> {
  const out = new Set<number>();
  const half = grid.pair / 2;
  for (const onset of onsets) {
    const k = pairIndex(onset, grid);
    const within = onset - k * grid.pair;
    const onPoint =
      Math.abs(within) < ON_GRID ||
      Math.abs(within - half) < ON_GRID ||
      Math.abs(within - grid.pair) < ON_GRID;
    if (!onPoint) out.add(k);
  }
  return out;
}

const cache = new WeakMap<object, Map<string, Set<number>>>();

/**
 * `straightPairs` of a track of notes, remembered per track and level (the
 * ratio does not change which pairs a part plays finer, and a swing slider
 * moves it continuously). `origin` is where the beat grid starts (a fractional
 * pickup moves every beat); pair indices count from there.
 */
export function straightPairsOf(
  track: readonly { beat: number }[],
  grid: SwingGrid,
  origin = 0,
): ReadonlySet<number> {
  let byGrid = cache.get(track);
  if (!byGrid) {
    byGrid = new Map();
    cache.set(track, byGrid);
  }
  const key = `${grid.pair}:${origin}`;
  let set = byGrid.get(key);
  if (!set) {
    set = straightPairs(
      track.map((note) => note.beat - origin),
      grid,
    );
    byGrid.set(key, set);
  }
  return set;
}

/**
 * Where a written position SOUNDS. The second note of a pair (its midpoint)
 * moves to the ratio when the pair swings; every other position stays put.
 * `swings(beat)` says whether the band swings at that beat (a feel cue or a
 * section may play straight); `straight` lists the pairs this part plays finer
 * than the level.
 */
export function swingPosition(
  position: number,
  grid: SwingGrid,
  swings: (beat: number) => boolean,
  straight?: ReadonlySet<number>,
): number {
  const k = pairIndex(position, grid);
  const start = k * grid.pair;
  if (Math.abs(position - start - grid.pair / 2) >= ON_GRID) return position;
  if (straight?.has(k)) return position;
  if (!swings(Math.floor(start + 1e-9))) return position;
  return start + grid.pair * grid.ratio;
}

/**
 * The kit's own swing (docs/swing-plan.md §4.1): a band that swings with the
 * kit puts each eighth "and" where the drum loop's skip note measured
 * (audio/loopSwing.ts), so the bass and the melody lock with the ride. Only
 * the triplet eighth follows the kit; a dotted or Scottish feel, or the
 * sixteenths, is an explicit choice and keeps its own ratio.
 */
export const KIT_FEEL: SwingFeel = 'triplet-8th';

/** The grid of a kit whose skip note sits `swing` of the way through the beat. */
export function kitGrid(swing: number): SwingGrid {
  return { pair: 1, ratio: swing };
}

/**
 * HOW MUCH a band swings against its kit (user request Oct 2026: a slider
 * under Kit for "more or less swing relative to the swing in the drum loop",
 * then one per part, "so if I want the bass to swing more"). Under the Kit
 * choice the band's "and" sits at the kit's skip note (the triplet where there
 * is none) plus the band's offset, and each part's at the band's plus its own.
 * An offset is a fraction of the beat added to the ratio, so it is relative:
 * a part keeps its distance from the kit when the loop changes. The drums are
 * a recording and keep the loop's own swing. The range runs from straight to
 * 7:1, the double-dotted figure (a double-dotted eighth and a thirty-second):
 * past it the "and" stops reading as a note of its own and becomes a flick
 * into the next beat. It was 4:1 until the user found every slider sitting near
 * the end with nowhere to go: the default kit already swings 2.9:1.
 */
export const SWING_RATIO_MIN = 0.5;
export const SWING_RATIO_MAX = 0.875;

/** The largest offset stored: half a beat, more than the range spans (the
 *  range itself holds what plays). */
export const SWING_OFFSET_LIMIT = 0.5;

/** The parts that swing against the band's swing; the drums keep the loop's. */
export type SwingPart = 'melody' | 'comp' | 'bass';

/** The band's offset from the kit, and each part's from the band. */
export interface SwingOffsets {
  band: number;
  melody: number;
  comp: number;
  bass: number;
}

export const NO_SWING_OFFSETS: SwingOffsets = { band: 0, melody: 0, comp: 0, bass: 0 };

/** The ratios a band following a kit may swing at: straight to 7:1, and
 *  always the kit's own, so an offset of 0 is the kit wherever it measured. */
export function swingRatioRange(kit: number): [number, number] {
  return [Math.min(SWING_RATIO_MIN, kit), Math.max(SWING_RATIO_MAX, kit)];
}

/**
 * Where an "and" sits under the Kit choice: the kit's ratio (`kit`, the
 * triplet when there is none) moved by the band's offset, then by the part's.
 * No part is the band's own swing. Each step is held to the range, so a part
 * slider starts from the band's ratio as the band slider shows it.
 */
export function kitSwingRatio(
  kit: number,
  offsets: SwingOffsets | undefined,
  part?: SwingPart,
): number {
  const [lo, hi] = swingRatioRange(kit);
  const hold = (ratio: number) => Math.min(hi, Math.max(lo, ratio));
  const band = hold(kit + (offsets?.band ?? 0));
  return part ? hold(band + (offsets?.[part] ?? 0)) : band;
}

/** A stored offset, or undefined for anything that is not one: a finite
 *  number, held to the range and to a thousandth of a beat. */
export function normalizeSwingOffset(value: unknown): number | undefined {
  if (typeof value !== 'number' || !Number.isFinite(value)) return undefined;
  const held = Math.min(SWING_OFFSET_LIMIT, Math.max(-SWING_OFFSET_LIMIT, value));
  return Math.round(held * 1000) / 1000 || 0;
}

/** A ratio as the app writes it, long to short: 2.9:1, 1.0:1 for straight. */
export function swingRatioLabel(ratio: number): string {
  return `${(ratio / (1 - ratio)).toFixed(1)}:1`;
}

/** What the Tempo panel offers: straight, the kit's swing, or one of the feels.
 *  A choice is three band fields, whether the band swings, how, and whether
 *  the triplet eighth follows the kit, mapped here in one place. */
export type SwingChoice = 'straight' | 'kit' | SwingFeel;

/** Every choice, as a literal tuple (the command schema needs one). */
export const SWING_CHOICE_IDS = [
  'straight',
  'kit',
  'triplet-8th',
  'triplet-16th',
  'dotted-8th',
  'dotted-16th',
  'scottish-8th',
  'scottish-16th',
] as const satisfies readonly SwingChoice[];

export function isSwingChoice(value: unknown): value is SwingChoice {
  return typeof value === 'string' && (SWING_CHOICE_IDS as readonly string[]).includes(value);
}

/** Whether a band's eighths follow the kit. A band stored before the field
 *  existed does: the kit is the default. */
export function followsKit(
  band: Pick<StyleBand, 'bassSwing' | 'swingFeel'> & { swingWithKit?: boolean },
): boolean {
  return (
    band.bassSwing &&
    (isSwingFeel(band.swingFeel) ? band.swingFeel : DEFAULT_SWING_FEEL) === KIT_FEEL &&
    band.swingWithKit !== false
  );
}

/** What a band plays. A band stored before the feel existed swings the triplet
 *  eighth, and one stored before the kit choice existed swings with the kit. */
export function swingChoiceOf(
  band: Pick<StyleBand, 'bassSwing' | 'swingFeel'> & { swingWithKit?: boolean },
): SwingChoice {
  if (!band.bassSwing) return 'straight';
  if (followsKit(band)) return 'kit';
  return isSwingFeel(band.swingFeel) ? band.swingFeel : DEFAULT_SWING_FEEL;
}

/** The band fields for a choice. Straight puts the feel back to the default,
 *  so a straight band reads the same as every straight preset. */
export function swingPatch(
  choice: SwingChoice,
): Pick<StyleBand, 'bassSwing' | 'swingFeel' | 'swingWithKit'> {
  if (choice === 'straight')
    return { bassSwing: false, swingFeel: DEFAULT_SWING_FEEL, swingWithKit: true };
  if (choice === 'kit') return { bassSwing: true, swingFeel: KIT_FEEL, swingWithKit: true };
  return { bassSwing: true, swingFeel: choice, swingWithKit: false };
}
